import { useMemo } from 'react';

import { AnalyticsProvider } from '@service-initiation/shared-data-access-analytics';
import {
  doesGivenPathEndWith,
  useCurrentPath,
  useFlowMetadata,
  usePrevSteps,
  useRootPath,
} from '@service-initiation/shared-data-access-flow-manager';
import { filterDuplicates } from '@service-initiation/shared-utils-helpers';
import { dispatcher } from '@service-initiation/tech-care-clients-analytics-dispatcher';
import { Route } from '@service-initiation/tech-care-data-access-routing';

import { useSanitizedGlobalUserState } from './hooks';
import { getEnumKeyByEnumValue } from './utils';

type TechCareAnalyticsProviderProps = {
  children: React.ReactNode;
};

function getEnumKeyByEndWithValue<T extends { [key: string]: string }>(
  theObject: T,
  value: string
) {
  return Object.keys(theObject).find((key) => value.endsWith(theObject[key]));
}

export function TechCareAnalyticsProvider({
  children,
}: TechCareAnalyticsProviderProps) {
  const sanitizedGlobalUserState = useSanitizedGlobalUserState();
  const { prevSteps } = usePrevSteps();
  const path = useCurrentPath();
  const currentStepName = getEnumKeyByEnumValue(Route, path);
  const { value: flowMetadata } = useFlowMetadata();
  const { value: flowRootPath } = useRootPath();
  const visitedSteps = filterDuplicates(
    flowMetadata.visitedPaths
      .filter((vs) => !doesGivenPathEndWith(vs, flowRootPath))
      .map((vs) => getEnumKeyByEndWithValue(Route, vs))
      .filter((vs) => vs)
  ).join('_');
  const visitedPaths = filterDuplicates(flowMetadata.visitedPaths);
  const globalExtraData = useMemo(
    () => ({
      ...sanitizedGlobalUserState,
      StepName: currentStepName,
      VisitedSteps: visitedSteps,
      prevSteps,
      step_count: flowMetadata.stepCount,
      visitedPaths,
    }),
    [
      currentStepName,
      flowMetadata.stepCount,
      visitedSteps,
      prevSteps,
      sanitizedGlobalUserState,
      visitedPaths,
    ]
  );

  return (
    <AnalyticsProvider dispatcher={dispatcher} extraData={globalExtraData}>
      {children}
    </AnalyticsProvider>
  );
}

export default TechCareAnalyticsProvider;
